import React from "react";
import G from "../libs/Glob.mjs";
import appLib from "../libs/appLib.mjs";
import ButtonsExitReloadSave from "../controls/ButtonsExitReloadSave.mjs";
import autoFormHelper from "./helperLibs/autoFormHelper.mjs";
import TextLabel from "../controls/TextLabel.mjs";
import "./AutoForm.css";
import AutoFormFields from "./AutoFormFields.mjs";

const prefix = 'AutoForm';

/**
 * Creates a formHelper object, for whole forms or field groups.
 * @param {object} state The form (or form) parameters
 * @param {string} state.itemID Mandaory, itemID
 * @param {string} [state.groupID] Optional
 * @param {Object[]} [state.formDef.fields] Fields definition - optional
 * @param {Object[]} [state.fields] Fields definition, alternative - optional
 * @param {Object} [state.groupInfo] Optional
 * @param {Object} [state.data] Optional
 * @param {function} [callback] If a function, the callback will be invoked once the necessary data have been loaded
 * @return {AutoFormHelperObject}
 */
export const makeFormHelper = ( state, callback ) =>{
	const init =  {
		itemID: state.itemID,
		groupID: state.groupID,
		fields: G.clone(state.formDef?.fields || state.fields),
		group: state.groupInfo,
		data: state.data
	};
	return new autoFormHelper(init, callback );
}

const AutoForm = class extends React.Component {
	constructor(props) {
		super(props);
		this.state = {
			itemID: G.getPageParam('item'),
			groupID: appLib.group(),
			data: props.data,
			changes: 0,
			loaded: false,
			user: G.getUserLoginName()
		}
		this.reload = this.reload.bind(this);
		this.autoLoad = this.autoLoad.bind(this);
		this.setChanges = this.setChanges.bind(this);
		this.checkValid = this.checkValid.bind(this);
	}

	componentDidMount() {
		this.uid = G.generateUid(prefix);
		G.register('FormValueChanged', (v)=>{
			this.setState({ data: v}, this.setChanges );
		}, this.uid);
		setTimeout( this.reload );
	}

	componentWillUnmount() {
		G.deregister(this.uid);
	}

	reload() {
		G.setStatus('pageTitle', G.label("Loading…"));
		G.trigger('pageButtons', <ButtonsExitReloadSave key={ Date.now() } exit={true} /> );
		this.setState(
			{ loaded: false, loading: true, data: undefined, helper: undefined },
			this.autoLoad
		);
	}

	/**
	 * Autoload components, automatically detached (timed-out)
	 * @private
	 * @param {boolean} [timedOut] If true it's already timed out
	 * @return {void}
	 * @summary Check status: user, groupID
	 * @summary Set status: formDef, groupInfo, data, loaded, loading
	 */
	autoLoad( timedOut ) {
		if ( ! timedOut ) {
			setTimeout( ()=> { this.autoLoad(true) } );
			return;
		}
		if ( ! this.state.user ) return G.gotoPage('Login');
		if ( ! this.state.groupID ) return G.gotoHomePage();
		if ( ! this.state.itemID ) return G.gotoHomePage();
		if ( this.state.loaded ) return;
		const ns = {};
		const errorHandler = err => {
			console.error(err);
			this.setState({loaded:true,loading:false});
			G.gotoHomePage();
		};
		if ( ! this.state.formDef ) {
			appLib.getFormInfo(
				this.state.groupID
			).then( data => {
				ns.formDef = data?.formDefinition;
				ns.groupInfo = data?.group;
				this.setState(ns, this.autoLoad)
			}).catch( errorHandler );
			return;
		}
		if ( ! this.state.data ) {
			appLib.getItemData(
				this.state.itemID
			).then(data => {
				ns.data = data || {};
				this.setState(ns, this.autoLoad);
			}).catch(errorHandler);
			return;
		}
		if (! this.state.helper ) {
			const helper = makeFormHelper(
				this.state,
				()=>{
					//helper.checkDependencies();
					helper.notify( v => { G.trigger('FormValueChanged', v) } );
					this.autoLoad();
				}
			);
			this.setState({helper: helper});
			return;
		}
		if ( this.state.loading ) {
			ns.loaded = true;
			ns.loading = false;
			G.setStatus('pageTitle', this.state.data?.name );
			G.trigger('pageButtons', (
				<ButtonsExitReloadSave
					key={ Date.now() }
					changesKey="FormDataChanges"
					validKey="FormDataIsValid"
					exit={ true }
					reload={ this.reload }
					save={ ()=>{
						this.state.helper.setValue('formControl_lastEdit', {
							editorLogin: G.getUserLogin(),
							editorName: G.getUserLoginName(),
							groupID: this.state.groupID,
							date: (new Date()).toISOString(),
							time: Date.now()
						}, true);
						appLib.setItemData(
							this.state.helper.getData(),
							this.state.itemID,
							this.state.groupID,
						).then( ()=>{
							this.setChanges(0);
						}).catch( e => {
							console.error(e);
						});
					} }
					download={ G.userIsAdmin() && (() => {
						G.saveJSON(
							this.state.data,
							(this.state.data?.name || this.state.itemID).replace(/[^a-z0-9-]+/gi,'_') + '.json'
						)
					})}
					upload = {G.userIsAdmin() && (() => {
						G.uploadJSON().then( data => {
							data.id = this.state.itemID;
							data.name = this.state.data.name;
							this.state.helper.setData( data );
						})
					})

					}
				/>
			));
			this.setState(ns, ()=>{ this.setChanges(0) }, 100);
		}
	}

	/**
	 * Set changes and triggers info
	 * @param {number} [qt]
	 * @return {void}
	 */
	setChanges( qt ) {
		if ( typeof qt !== "number") qt = this.state.changes + 1;
		this.setState({ changes: qt }, ()=>{
			G.trigger("FormDataChanges", qt);
			setTimeout(this.checkValid, 100);
		} );
	}

	checkValid(count = 0) {
		if ( count > 5 ) return;
		const d = document.getElementById(this.uid);
		if ( d ) {
			const qtErrors = d.querySelectorAll('.AutoFieldLabel > h6').length;
			this.state.helper.setValue('formControl_isComplete', (qtErrors === 0 ? "Y" : "N"), true);
			this.state.helper.setValue('formControl_missingFields', qtErrors, true);
			G.trigger("FormDataIsValid", qtErrors ? "⚠︎"+qtErrors : true );
		} else {
			setTimeout( ()=> { this.checkValid(count +1); }, 500 );
		}
	}

	render() {
		if ( this.state.loading ) return <div><TextLabel value={"Loading…"} /></div>;
		return(
			<div className={prefix} id={this.uid}>
				<AutoFormFields
					fields={ G.clone(this.state.formDef?.fields || []) }
					helper={ this.state.helper }
					topHelper={ this.state.helper }
					data={ this.state.data }
				/>
			</div>
		);
	}
}

export default AutoForm;